import React, { useEffect, useState } from "react";
import Highcharts from "highcharts/highcharts-more"; // Import as side-effect to register arearange

const AreaRangeChart = ({ stationData, selectedStation, loading, dataType }) => {

    const [chartData, setChartData] = useState(null);
    const [lineData, setLineData] = useState(null);

    useEffect(() => {
        if (stationData) {
            getData();
        }
    }, [stationData]);

    const getData = () => {
        const data = stationData.forecast2.forecast;

        const newChartData = data.map(item => [
            new Date(item.date).getTime(),
            item.flow_p25,
            item.flow_p75
        ]);

        const newLineData = data.map(item => [
            new Date(item.date).getTime(),
            item.flow
        ]);

        setChartData(newChartData);
        setLineData(newLineData);
    };

    const processReturnPeriodData = (returnPeriod, chartData) => {
        const minFlow = Math.min(...chartData.map(d => d[1]), 0); // Include 0 as fallback
        const maxFlow = Math.max(...chartData.map(d => d[2]));

        return returnPeriod.reduce((acc, item, index) => {
            let currentFlow = item.flow ?? null;
            let previousFlow = index === 0 ? 0 : returnPeriod[index - 1].flow ?? null;

            // If currentFlow is above the previous category, assign it to the next category
            if (currentFlow !== null && currentFlow < minFlow) {
                currentFlow = minFlow;
            }

            // Check if flow is within range or needs to extend to next category
            if (currentFlow !== null && currentFlow !== previousFlow) {
                acc.push({
                    from: previousFlow ?? 0,
                    to: currentFlow,
                    color: item.color,
                });

                // 🔹 If the current flow exceeds the max of the current category, extend it
                if (currentFlow > maxFlow && index < returnPeriod.length - 1) {
                    acc.push({
                        from: currentFlow,
                        to: returnPeriod[index + 1].flow ?? maxFlow,
                        color: returnPeriod[index + 1]?.color ?? item.color, // Use next flood category color
                    });
                }
            }
            return acc;
        }, []);
    };

    useEffect(() => {
        getPlotChart();
    }, [chartData, lineData]);

    const getPlotChart = () => {
        if (!stationData || !chartData || !lineData) return;

        const returnPeriodData = processReturnPeriodData(stationData.forecast2.return_periods, chartData);

        // Find min and max flow values from return periods
        const flowValues = stationData.forecast2.return_periods.map(item => item.flow ?? 0);
        const minFlow = Math.min(...flowValues, 0); // Include 0 as a fallback
        const maxFlow = Math.max(...flowValues);

        let verticalLineDate, xAxisType, xAxisFormatter;


        verticalLineDate = new Date().getTime();
        xAxisType = 'datetime';
        xAxisFormatter = function () {
            return Highcharts.dateFormat('%b %d, %Y', this.value); // Show full date
        };

        const chart = Highcharts.chart('station-graph-area', {
            chart: {
                type: 'arearange',
                height: 252, // Set fixed height
                scrollablePlotArea: {
                    minWidth: 600,
                    scrollPositionX: 1
                }
            },
            title: {
                text: 'Seasonal range',
                align: 'left',
                style: {
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#105586'
                }
            },
            xAxis: {
                type: xAxisType,
                title: { text: 'Date' },
                labels: { formatter: xAxisFormatter },
                plotLines: [
                    {
                        value: verticalLineDate,
                        color: 'black',
                        width: 1,
                        zIndex: 10,
                        label: {
                            align: 'left',
                            verticalAlign: 'bottom',
                            style: { color: 'red' }
                        }
                    }
                ]
            },
            yAxis: {
                title: { text: 'Flow (m³/s)' },
                // min: minFlow,
                // max: maxFlow,
                startOnTick: false,
                endOnTick: false,
                // softMin: minFlow - 5,
                // softMax: maxFlow + 5,
                labels: {
                    useHTML: true,
                    style: {
                        fontWeight: 'bold',
                        background: 'rgba(255, 255, 255, 0.7)',
                        padding: '2px',
                        borderRadius: '3px'
                    }
                },
                gridLineWidth: 0,
                plotBands: returnPeriodData
            },
            tooltip: {
                shared: true,
                valueSuffix: ' m³/s'
            },
            plotOptions: {
                arearange: {
                    fillOpacity: 1,
                    lineWidth: 2,
                    marker: {
                        enabled: false
                    }
                },
                line: {
                    lineWidth: 2,
                    marker: {
                        enabled: false,
                        radius: 1
                    }
                }
            },
            legend: {
                enabled: false
            },
            credits: { enabled: false },
            series: [
                {
                    name: 'Normal Range',
                    data: chartData,
                    type: 'arearange',
                    color: 'rgba(173, 216, 230, 0.7)',  // Light blue with 30% opacity
                    zIndex: 1
                },
                {
                    name: 'Forecast Flow',
                    data: lineData,
                    type: 'line',
                    color: '#3125be',
                    zIndex: 2
                }
            ],
            exporting: {
                enabled: false,
                fallbackToExportServer: false, // 🚫 Prevents Highcharts from using online export
                libURL: 'https://code.highcharts.com/modules/',// ✅ Uses local scripts for export
                filename: 'Flow_Forecast_Chart',
                buttons: {
                    contextButton: {
                        menuItems: [
                            'downloadPNG',
                            'downloadJPEG',
                            'downloadPDF',
                            'downloadSVG'
                        ]
                    }
                },
                chartOptions: {
                    title: {
                        text: 'Flow Forecast - Exported'
                    }
                }
            },
            navigation: {
                menuItemStyle: { fontSize: '10px' }
            }
        });

        return () => {
            chart.destroy();
        };
    };

    return (
        <div className="d-flex flex-wrap legend-text mb-0 pb-0" id="station-graph-area"></div>
    );
};

export default AreaRangeChart;